import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gate } from '../permissions/access';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import SettingsPageLayout from './SettingsPageLayout';
import { useDispatch, useSelector } from 'react-redux';
import { diarySharingActions } from '../../_actions/diarySharing.actions';
import '../shared/ui.css';
import './DiarySharePanel.css';

const ROLE_LABEL = { 1: 'Employed', 2: 'Self-employed' };

const CheckIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const CrossIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const ModeDropdown = ({ value, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const MODE_LABELS = { everyone: 'Everyone', selected: 'Selected', nobody: 'Nobody' };

    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        document.addEventListener('touchstart', handler);
        return () => {
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('touchstart', handler);
        };
    }, [open]);

    return (
        <div className="dsp-dropdown" ref={ref}>
            <button type="button" className="dsp-dropdown-trigger" onClick={() => setOpen((v) => !v)}>
                <span>{MODE_LABELS[value] || value}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 4.5L6 7.5L9 4.5" />
                </svg>
            </button>
            {open && (
                <div className="dsp-dropdown-menu">
                    {['everyone', 'selected', 'nobody'].map((mode) => (
                        <button key={mode} type="button"
                            className={`dsp-dropdown-item ${value === mode ? 'dsp-dropdown-item--active' : ''}`}
                            onClick={() => { onChange(mode); setOpen(false); }}>
                            {value === mode && <CheckIcon />}
                            <span>{MODE_LABELS[mode]}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const StaffMemberPicker = ({ allStaff, selected, onChange, ownId }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const inputRef = useRef(null);

    useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

    const candidates = useMemo(() => {
        const lower = search.toLowerCase();
        return allStaff.filter((s) => s.hairdresserId !== ownId).filter((s) => !lower || s.name.toLowerCase().includes(lower));
    }, [allStaff, ownId, search]);

    const selectedSet = useMemo(() => new Set(selected), [selected]);
    const nameById = useMemo(() => { const m = {}; allStaff.forEach((s) => { m[s.hairdresserId] = s.name; }); return m; }, [allStaff]);

    const toggle = (id) => {
        if (selectedSet.has(id)) onChange(selected.filter((x) => x !== id));
        else onChange([...selected, id]);
    };

    if (!open) {
        return (
            <div className="dsp-selected-row">
                <div className="dsp-selected-chips">
                    {selected.length === 0 && <span className="dsp-selected-empty">No one selected</span>}
                    {selected.slice(0, 4).map((id) => <span key={id} className="dsp-chip">{nameById[id] || id}</span>)}
                    {selected.length > 4 && <span className="dsp-chip dsp-chip--more">+{selected.length - 4}</span>}
                </div>
                <button type="button" className="dsp-edit-btn" onClick={() => setOpen(true)}>Edit</button>
            </div>
        );
    }

    return (
        <div className="dsp-picker">
            <input ref={inputRef} type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search team members..." className="dsp-picker-input" />
            <div className="dsp-picker-list">
                {candidates.map((s) => (
                    <button key={s.hairdresserId} type="button"
                        className={`dsp-picker-item ${selectedSet.has(s.hairdresserId) ? 'dsp-picker-item--selected' : ''}`}
                        onClick={() => toggle(s.hairdresserId)}>
                        <span className="dsp-picker-avatar">{(s.name || '?').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}</span>
                        <span className="dsp-picker-name">{s.name}</span>
                        {selectedSet.has(s.hairdresserId) && <span className="dsp-picker-check"><CheckIcon /></span>}
                    </button>
                ))}
                {candidates.length === 0 && <p className="dsp-picker-empty">No matches</p>}
            </div>
            <button type="button" className="dsp-picker-done" onClick={() => { setOpen(false); setSearch(''); }}>Done</button>
        </div>
    );
};

/**
 * DiarySharePage — full settings sub-page for self-employed users.
 * Uses the same layout pattern as Billing/Payouts: TopHeader with
 * back arrow, SideNav, content area. Connected via withRouter + connect
 * so it matches the routing expectations of other settings pages.
 */
/**
 * The diary sharing content on its own, so Settings › Sharing can show it as
 * its Diary tab. `embedded` drops the page heading (the tab names it).
 */
export function DiarySharingSection({ embedded, ...props }) {
    const dispatch = useDispatch();
    const { bulkShare, staff, loading } = useSelector((s) => s.diarySharing);

    const hairdresser = props.user?.userInfo?.hairdresser || props.user?.userInfo?.hairdresserInfo;
    // Roles & permissions: `settings` manages employed staff's visibility too.
    const isManager = hairdresser?.isSalonOwner === 1 || gate('settings', false);
    const isSelfEmployed = hairdresser?.employmentTypeId === 2;
    // Rows are keyed by hairdresser id; localStorage 'userId' is the User id,
    // so match on either. Matching on userId alone meant self-employed staff
    // never found their own row and got a page with nothing to change.
    const myUserId = localStorage.getItem('userId');
    const myHairdresserId = hairdresser?._id || hairdresser?.hairdresserId;

    useEffect(() => { dispatch(diarySharingActions.fetchSettings()); }, [dispatch]);

    const handleBulkToggle = useCallback((val) => {
        dispatch(diarySharingActions.toggleBulkShare(val));
    }, [dispatch]);

    const handleModeChange = useCallback((staffId, mode) => {
        dispatch(diarySharingActions.updateStaffVisibility(staffId, { diaryVisibility: mode, diaryVisibleTo: [] }));
    }, [dispatch]);

    const handleSelectedChange = useCallback((staffId, newSelected) => {
        dispatch(diarySharingActions.updateStaffVisibility(staffId, { diaryVisibility: 'selected', diaryVisibleTo: newSelected }));
    }, [dispatch]);

    const canEdit = (s) => {
        if (isManager && s.employmentTypeId !== 2) return true;
        if (isSelfEmployed && s.hairdresserId === myId) return true;
        return false;
    };

    const myRow = staff.find((s) => (myHairdresserId && s.hairdresserId === String(myHairdresserId))
        || (myUserId && s.userId === myUserId));
    const myId = myRow ? myRow.hairdresserId : myHairdresserId;
    const visibleStaff = isManager
        ? staff.filter((s) => s.hairdresserId !== myId)
        : isSelfEmployed ? [myRow].filter(Boolean) : [];

    const Toggle = ({ checked, onChange }) => (
        <button type="button" className={'ds-toggle' + (checked ? ' ds-toggle--on' : '')} onClick={() => onChange(!checked)}>
            <span className="ds-toggle-knob" />
        </button>
    );

    return (
            <div className="dsp-page-content">
                <div className="dsp-page-intro">
                    {!embedded && <h2 className="dsp-page-title">Diary sharing</h2>}
                    <p className="dsp-page-desc">
                        {isManager
                            ? 'Control who can see each team member\'s diary and add appointments on their behalf.'
                            : 'Choose who can see your diary and add appointments on your behalf.'}
                    </p>
                </div>

                {loading && staff.length === 0 && (
                    <div className="dsp-loading">Loading...</div>
                )}

                {!isManager && !loading && !bulkShare && (
                    <div className="dsp-bulk-card" style={{ marginBottom: 24 }}>
                        <div className="dsp-bulk-text">
                            <span className="dsp-bulk-title">Team diary sharing is off</span>
                            <span className="dsp-bulk-desc">You'll only see your own diary until your salon owner turns on "Share business diaries with the team" here in Settings › Sharing.</span>
                        </div>
                    </div>
                )}

                {/* Bulk toggle — manager only */}
                {isManager && (
                    <div className="dsp-bulk-card" style={{ marginBottom: 24 }}>
                        <div className="dsp-bulk-text">
                            <span className="dsp-bulk-title">Share business diaries with the team</span>
                            <span className="dsp-bulk-desc">Your diary and all employed staff diaries are visible to everyone. Self-employed team members manage their own sharing.</span>
                        </div>
                        <Toggle checked={bulkShare} onChange={handleBulkToggle} />
                    </div>
                )}

                {/* Self-employed: own diary card */}
                {isSelfEmployed && myRow && (
                    <div className="settings-card">
                        <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 6px', color: '#29282D' }}>Your diary</h3>
                        <p style={{ fontSize: 13, color: '#6e6e73', margin: '0 0 18px', lineHeight: 1.5 }}>
                            Choose who can view your appointments and book on your behalf.
                        </p>
                        <div className="dsp-mode-row" style={{ marginBottom: 12 }}>
                            <span className="dsp-mode-label">Diary visible to</span>
                            <ModeDropdown value={myRow.diaryVisibility} onChange={(mode) => handleModeChange(myRow.hairdresserId, mode)} />
                        </div>
                        {myRow.diaryVisibility === 'selected' && (
                            <StaffMemberPicker allStaff={staff} selected={myRow.diaryVisibleTo || []} onChange={(newSelected) => handleSelectedChange(myRow.hairdresserId, newSelected)} ownId={myRow.hairdresserId} />
                        )}
                        {myRow.diaryVisibility === 'everyone' && (
                            <p style={{ fontSize: 13, color: '#8e8e93', margin: '4px 0 0' }}>All team members can see your diary and add appointments.</p>
                        )}
                        {myRow.diaryVisibility === 'nobody' && (
                            <p style={{ fontSize: 13, color: '#8e8e93', margin: '4px 0 0' }}>Your diary is hidden from all team members.</p>
                        )}
                    </div>
                )}

                {/* Staff list — manager sees all with controls, SE sees read-only reciprocity */}
                {visibleStaff.length > 0 && (isManager ? (
                    <>
                        <p className="beu-section-label" style={{ margin: '24px 0 14px' }}>Team members</p>
                        <div className="settings-card" style={{ padding: '4px 24px' }}>
                            {visibleStaff.map((member) => {
                                const editable = canEdit(member) && !bulkShare;
                                return (
                                    <div key={member.hairdresserId} className="dsp-staff-row">
                                        <div className="dsp-staff-header">
                                            <div className="dsp-staff-avatar">
                                                {(member.name || '?').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                                            </div>
                                            <div className="dsp-staff-meta">
                                                <span className="dsp-staff-name">{member.name}</span>
                                                <span className="dsp-staff-role">{ROLE_LABEL[member.employmentTypeId] || ''}</span>
                                            </div>
                                        </div>
                                        <div className="dsp-staff-controls">
                                            {bulkShare && member.employmentTypeId !== 2 ? (
                                                <div className="dsp-readonly-row">
                                                    <span className="dsp-readonly-badge">Shared with everyone</span>
                                                </div>
                                            ) : editable ? (
                                                <div className="dsp-mode-row">
                                                    <span className="dsp-mode-label">Diary visible to</span>
                                                    <ModeDropdown value={member.diaryVisibility} onChange={(mode) => handleModeChange(member.hairdresserId, mode)} />
                                                </div>
                                            ) : (
                                                <div className="dsp-readonly-row">
                                                    <span className="dsp-readonly-note">Managed by {member.name.split(' ')[0]}</span>
                                                </div>
                                            )}
                                            {editable && !bulkShare && member.diaryVisibility === 'selected' && (
                                                <StaffMemberPicker allStaff={staff} selected={member.diaryVisibleTo || []} onChange={(newSelected) => handleSelectedChange(member.hairdresserId, newSelected)} ownId={member.hairdresserId} />
                                            )}
                                            {member.hairdresserId !== myId && (
                                                <div className={`dsp-reciprocity ${member.sharesWithCurrentUser ? 'dsp-reciprocity--yes' : 'dsp-reciprocity--no'}`}>
                                                    {member.sharesWithCurrentUser ? <><CheckIcon /> <span>Shares their diary with you</span></> : <><CrossIcon /> <span>Does not share their diary with you</span></>}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <p className="dsp-manager-note">You can always see all diaries as the salon owner.</p>
                    </>
                ) : (
                    <div className="settings-card" style={{ marginTop: 20 }}>
                        <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 14px', color: '#29282D' }}>Team diary visibility</h3>
                        <p style={{ fontSize: 13, color: '#6e6e73', margin: '0 0 18px', lineHeight: 1.5 }}>See which team members share their diary with you.</p>
                        {staff.filter((s) => s.hairdresserId !== myId).map((member) => (
                            <div key={member.hairdresserId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderTop: '1px solid #f0f0f2' }}>
                                <div className="dsp-staff-avatar" style={{ width: 32, height: 32, fontSize: 11 }}>
                                    {(member.name || '?').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                                </div>
                                <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: '#29282D' }}>{member.name}</span>
                                <div className={`dsp-reciprocity ${member.sharesWithCurrentUser ? 'dsp-reciprocity--yes' : 'dsp-reciprocity--no'}`}>
                                    {member.sharesWithCurrentUser ? <CheckIcon /> : <CrossIcon />}
                                    <span>{member.sharesWithCurrentUser ? 'Shares with you' : 'Not sharing'}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
    );
}

function DiarySharePage(props) {
    return (
        <SettingsPageLayout activeTab="diary-sharing" {...props}>
            <DiarySharingSection {...props} />
        </SettingsPageLayout>
    );
}

function mapStateToProps(state) {
    return { ...state };
}

export default withRouter(connect(mapStateToProps)(DiarySharePage));
